import { type NMPLocale } from "@borealis/locale-common";
import { getNumberAsCurrencyFormatter, getNumberFormatter, type CurrencyCode } from "@borealis/number-format";
import { i18n } from "@lingui/core";
import IconFilter16 from "@warp-ds/icons/react/filter-16";
import { Affix, Attention, Button, Pill, TextArea, TextField } from "@warp-ds/react";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { SemanticFilter, SemanticFilterItem } from "src/types/SemanticFilter";
import { AppConfigContext } from "../../AppConfig";
import { trackSemanticSearchLandingView } from "../../tracking/pulse";
import {
	clearSearchStore,
	setFiltersModalOpen,
	setSearchInputValue,
	setSearchQuery,
	setSearchResults,
} from "../stores/searchStore";
import { FiltersModal } from "./FiltersModal";
import { useAbortableRequest } from "./hooks/useAbortableRequest";
import { useSearchCallout } from "./hooks/useSearchCallout";
import { useSearchStore } from "./hooks/useSearchStore";
import { useUrlSync } from "./hooks/useUrlSync";
import "./SearchBar.css";
import { fetchRelatedSearches, fetchSemanticSuggestions } from "./semanticClient";

const resolveNMPLocale = (locale: string): NMPLocale => {
	const normalized = locale.toLowerCase();
	const base = normalized.split("-")[0];
	if (base === "no") return "nb";
	if (base === "nb" || base === "sv" || base === "da" || base === "fi" || base === "en") {
		return base;
	}
	return "en";
};

export type SearchBarProps = {
	onChange?: () => void;
	isMobile?: boolean;
	currency?: string;
	supportedFilterNames?: string[];
	disableMargin?: boolean;
	useCompact?: boolean;
};

export function SearchBar(props: SearchBarProps) {
	const {
		onChange,
		isMobile,
		currency: currencyProp,
		supportedFilterNames: supportedFilterNamesProp,
		disableMargin,
		useCompact = false,
	} = props;
	const storeState = useSearchStore();
	const appConfig = useContext(AppConfigContext);
	const currency = currencyProp ?? appConfig?.currency ?? "EUR";
	const resolvedCurrency = currency && currency.trim() ? currency : "EUR";
	const supportedFilterNames = supportedFilterNamesProp ?? appConfig?.supportedFilterNames;
	const [queryValue, setQueryValue] = useState(() => storeState.inputValue ?? storeState.q ?? "");
	const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
	const [pendingFilterOpen, setPendingFilterOpen] = useState<{ name: string; focusValue?: unknown } | null>(null);
	const [urlSearch, setUrlSearch] = useState(() => (typeof window === "undefined" ? "" : window.location.search));
	const { executeRequest } = useAbortableRequest();
	const { showCallout, handleDismiss: handleDismissCallout } = useSearchCallout(queryValue);
	const lastFetchedQueryRef = useRef("");
	const hasTrackedLandingRef = useRef(false);
	const isTypingRef = useRef(false);
	const textFieldRef = useRef<HTMLTextAreaElement | null>(null);
	const [placeholderSuggestion, setPlaceholderSuggestion] = useState<string | undefined>();
	const filters = storeState.filters ?? [];
	const hasQuery = Boolean((storeState.q ?? "").trim());
	const queryForApply = queryValue || storeState.q || "";

	const resolvedLocale = useMemo(() => resolveNMPLocale(i18n.locale), [i18n.locale]);
	const numberFormatter = useMemo(
		() =>
			getNumberFormatter(resolvedLocale, {
				minimumFractionDigits: 0,
				maximumFractionDigits: 0,
			}),
		[resolvedLocale],
	);
	const currencyFormatter = useMemo(
		() =>
			getNumberAsCurrencyFormatter(resolvedLocale, {
				minimumFractionDigits: 0,
				maximumFractionDigits: 0,
			}),
		[resolvedLocale],
	);
	const formatNumberValue = useCallback((value: number) => numberFormatter(value), [numberFormatter]);
	const formatCurrencyValue = useCallback(
		(value: number) => currencyFormatter(value, resolvedCurrency as CurrencyCode),
		[currencyFormatter, resolvedCurrency],
	);
	const formatRangeValue = useCallback(
		(value: number, filter: SemanticFilter) => {
			if (filter.name === "price") {
				if (filter.unit) {
					return `${formatNumberValue(value)} ${filter.unit}`;
				}
				return formatCurrencyValue(value);
			}
			if (filter.unit) {
				return `${formatNumberValue(value)} ${filter.unit}`;
			}
			return formatNumberValue(value);
		},
		[formatCurrencyValue, formatNumberValue],
	);

	const openFiltersModal = useCallback((name?: string, focusValue?: unknown) => {
		if (name) {
			setPendingFilterOpen({ name, focusValue });
		} else {
			setPendingFilterOpen(null);
		}
		setIsFiltersModalOpen(true);
		setFiltersModalOpen(true);
	}, []);

	const closeFiltersModal = useCallback(() => {
		setIsFiltersModalOpen(false);
		setFiltersModalOpen(false);
		setPendingFilterOpen(null);
	}, []);

	// Helper to read filters from current URL
	const getFiltersFromUrl = useCallback(() => {
		if (typeof window === "undefined") return {};
		const params = new URLSearchParams(window.location.search);
		const filterParams: Record<string, unknown> = {};

		params.forEach((value, key) => {
			if (key !== "q" && key !== "limit" && key !== "eqe") {
				const existing = filterParams[key];
				if (existing) {
					if (Array.isArray(existing)) {
						existing.push(value);
					} else {
						filterParams[key] = [existing, value];
					}
				} else {
					filterParams[key] = value;
				}
			}
		});

		return filterParams;
	}, []);

	useUrlSync(storeState.q ?? "");

	useEffect(() => {
		if (typeof window === "undefined") return;
		if (hasTrackedLandingRef.current) return;
		trackSemanticSearchLandingView();
		hasTrackedLandingRef.current = true;
	}, []);

	useEffect(() => {
		if (typeof window === "undefined") return;
		let isActive = true;
		void (async () => {
			try {
				const { results } = await fetchRelatedSearches(undefined, 1);
				if (!isActive) return;
				const nextPlaceholder = results?.[0]?.query?.trim();
				if (nextPlaceholder) {
					setPlaceholderSuggestion(nextPlaceholder);
				}
			} catch {
				// Ignore placeholder fetch failures; fallback copy remains in place.
			}
		})();
		return () => {
			isActive = false;
		};
	}, []);

	const fetchSuggestions = useCallback(
		async (query: string) => {
			if (!query.trim()) {
				return;
			}

			await executeRequest(async () => {
				// Get filters from URL to include in search
				const urlFilters = getFiltersFromUrl();
				const eqeParam =
					typeof window === "undefined" ? undefined : new URLSearchParams(window.location.search).get("eqe");
				const eqeValue = eqeParam === "false" ? false : undefined;
				const response = await fetchSemanticSuggestions(query, undefined, urlFilters, eqeValue);
				setSearchResults(response.results, query, response.error, response.filters, response.tracking);
			}).catch(() => {
				setSearchResults([], query, {
					hasError: true,
					errorType: "network",
				});
			});
		},
		[executeRequest, getFiltersFromUrl],
	);

	useEffect(() => {
		const nextValue = storeState.inputValue ?? storeState.q ?? "";

		setQueryValue((currentValue) => {
			if (nextValue !== currentValue) {
				return nextValue;
			}
			return currentValue;
		});

		if (storeState.q && storeState.q !== lastFetchedQueryRef.current) {
			lastFetchedQueryRef.current = storeState.q;
			void fetchSuggestions(storeState.q);
		} else if (!storeState.q) {
			lastFetchedQueryRef.current = "";
		}
	}, [storeState, fetchSuggestions]);

	useEffect(() => {
		if (typeof window === "undefined") return;

		const params = new URLSearchParams(window.location.search);
		const initialQuery = params.get("q");

		if (initialQuery !== null) {
			setSearchQuery(initialQuery);
		}

		// Handle browser back/forward navigation
		const handlePopState = () => {
			const params = new URLSearchParams(window.location.search);
			const query = params.get("q") || "";
			if (query) {
				setSearchQuery(query);
			} else {
				clearSearchStore();
			}
			setUrlSearch(window.location.search);
		};

		window.addEventListener("popstate", handlePopState);

		return () => {
			window.removeEventListener("popstate", handlePopState);
		};
	}, []);

	const findLabel = useCallback(
		(items: NonNullable<SemanticFilter["filter_items"]> | undefined, value: unknown): string | undefined => {
			if (!items) return undefined;
			for (const item of items) {
				const candidate = item.value ?? item.name ?? item.display_name;
				if (candidate === value) {
					return item.display_name ?? item.name ?? String(value);
				}
				if (Array.isArray(item.filter_items) && item.filter_items.length > 0) {
					const nested = findLabel(item.filter_items, value);
					if (nested) return nested;
				}
			}
			return undefined;
		},
		[],
	);

	const buildSelectionsFromUrl = useCallback(
		(params: URLSearchParams) => {
			const selections: Record<string, unknown> = {};
			filters.forEach((filter) => {
				const name = filter.name ?? "";
				if (!name || name === "q") return;

				if (filter.type === "STANDARD_FILTER") {
					const values = params.getAll(name);
					if (values.length > 0) {
						selections[name] = values;
					}
					return;
				}

				if (filter.type === "RANGE_FILTER") {
					const fromName = filter.name_from ?? `${name}_from`;
					const toName = filter.name_to ?? `${name}_to`;
					const fromVal = params.get(fromName);
					const toVal = params.get(toName);
					if (fromVal === null && toVal === null) return;

					const min = fromVal !== null && !Number.isNaN(Number(fromVal)) ? Number(fromVal) : undefined;
					const max = toVal !== null && !Number.isNaN(Number(toVal)) ? Number(toVal) : undefined;
					if (min === undefined && max === undefined) return;
					selections[name] = [min, max];
				}
			});
			return selections;
		},
		[filters],
	);

	const handleChange = useCallback(
		(value: string) => {
			if (onChange) onChange();
			setQueryValue(value);
			setSearchInputValue(value);

			// If the value becomes empty and we're NOT typing (clear button clicked), clear the store
			if (!value && queryValue && !isTypingRef.current) {
				lastFetchedQueryRef.current = "";
				clearSearchStore();
			}

			// Reset the typing flag after handling
			isTypingRef.current = false;
		},
		[onChange, queryValue],
	);

	const handleClearSearch = useCallback(() => {
		isTypingRef.current = false;
		setQueryValue("");
		setSearchInputValue("");
		lastFetchedQueryRef.current = "";
		clearSearchStore();
		if (textFieldRef.current) {
			textFieldRef.current.focus();
		}
	}, []);

	const handleSubmit = useCallback(
		(value: string) => {
			const trimmedValue = value.trim();

			if (trimmedValue) {
				setSearchQuery(value);
				lastFetchedQueryRef.current = trimmedValue;
				void fetchSuggestions(trimmedValue);
			} else {
				// Only clear when submitting an empty search
				lastFetchedQueryRef.current = "";
				clearSearchStore();
			}
		},
		[fetchSuggestions],
	);

	const containerClassName = [
		"flex flex-col items-start w-full px-0 md:px-48",
		useCompact ? "gap-x-12" : "gap-12",
		!disableMargin && "mt-24",
	]
		.filter(Boolean)
		.join(" ");

	const handleApplyFilters = useCallback(
		async (selections: Record<string, unknown>) => {
			if (!queryForApply) return;

			const params = new URLSearchParams(window.location.search);
			params.set("q", queryForApply);

			const filterParams: Record<string, unknown> = {};

			filters.forEach((filter) => {
				const name = filter.name ?? "";
				if (!name) return;
				const selection = selections[name];

				if (filter.type === "STANDARD_FILTER") {
					let values = Array.isArray(selection) ? selection : [];
					// Special-case numeric lists like trade_type
					if (name === "trade_type") {
						const numeric = values.map((v) => Number(v)).filter((num) => typeof num === "number" && !Number.isNaN(num));
						if (numeric.length === values.length) {
							values = Array.from(new Set(numeric)).sort((a, b) => a - b);
						}
					}
					if (values.length > 0) {
						filterParams[name] = values;
						params.delete(name);
						values.forEach((val) => {
							if (val !== undefined && val !== null && val !== "") {
								params.append(name, String(val));
							}
						});
					} else {
						params.delete(name);
					}
				} else if (filter.type === "RANGE_FILTER") {
					const range = Array.isArray(selection) && selection.length === 2 ? selection : undefined;
					const fromName = filter.name_from ?? `${name}_from`;
					const toName = filter.name_to ?? `${name}_to`;
					if (range) {
						const [fromRaw, toRaw] = range;
						const hasFrom = typeof fromRaw === "number" && !Number.isNaN(fromRaw);
						const hasTo = typeof toRaw === "number" && !Number.isNaN(toRaw);

						if (hasFrom || hasTo) {
							if (hasFrom) {
								filterParams[fromName] = fromRaw;
								params.set(fromName, String(fromRaw));
							} else {
								params.delete(fromName);
							}

							if (hasTo) {
								filterParams[toName] = toRaw;
								params.set(toName, String(toRaw));
							} else {
								params.delete(toName);
							}
						} else {
							params.delete(fromName);
							params.delete(toName);
						}
					} else {
						params.delete(fromName);
						params.delete(toName);
					}
				}
			});

			const newUrl = `${window.location.pathname}?${params.toString()}`;
			window.history.pushState({}, "", newUrl);
			setUrlSearch(`?${params.toString()}`);

			const eqeParam = params.get("eqe");
			const eqeValue = eqeParam === "false" ? false : undefined;

			await executeRequest(async () => {
				const response = await fetchSemanticSuggestions(queryForApply, undefined, filterParams, eqeValue);
				setSearchResults(response.results, queryForApply, response.error, response.filters, response.tracking);
			}).catch(() => {
				setSearchResults([], queryForApply, {
					hasError: true,
					errorType: "network",
				});
			});
			closeFiltersModal();
		},
		[closeFiltersModal, executeRequest, filters, queryForApply],
	);

	const textFieldClassName = "w-full";
	const updateUrlSearchState = useCallback(
		(params: URLSearchParams) => {
			const nextQuery = params.toString();
			const newUrl = nextQuery ? `${window.location.pathname}?${nextQuery}` : window.location.pathname;
			window.history.pushState({}, "", newUrl);
			setUrlSearch(nextQuery ? `?${nextQuery}` : "");
		},
		[setUrlSearch],
	);
	const filterPills = useMemo(() => {
		if (typeof window === "undefined" || !urlSearch) return [];
		const params = new URLSearchParams(urlSearch);
		const pills: Array<{
			key: string;
			label: string;
			filterName: string;
			focusValue?: unknown;
			onRemove?: () => void;
		}> = [];
		const handledKeys = new Set<string>();

		if (filters.length > 0) {
			filters.forEach((filter) => {
				const name = filter.name ?? "";
				if (!name || name === "q") return;

				if (filter.type === "STANDARD_FILTER") {
					const values = params.getAll(name);
					if (values.length > 0) {
						handledKeys.add(name);
					}
					const hasNested = (filter.filter_items ?? []).some(
						(item: SemanticFilterItem) => Array.isArray(item.filter_items) && item.filter_items.length > 0,
					);
					if (hasNested) {
						const selectedSet = new Set(values);
						const topItems: SemanticFilterItem[] = filter.filter_items ?? [];

						const getItemValue = (item: SemanticFilterItem) => item.value ?? item.name ?? item.display_name ?? "";
						const getItemLabel = (item: SemanticFilterItem) =>
							item.display_name ?? item.name ?? String(item.value ?? "");

						const collectSubtreeValues = (item: SemanticFilterItem): string[] => {
							const subtreeValues: string[] = [];
							const value = getItemValue(item);
							if (value !== "") subtreeValues.push(String(value));
							const children: SemanticFilterItem[] = item.filter_items ?? [];
							if (children.length > 0) {
								children.forEach((child: SemanticFilterItem) => {
									subtreeValues.push(...collectSubtreeValues(child));
								});
							}
							return subtreeValues;
						};
						const collectLeafValues = (item: SemanticFilterItem): string[] => {
							const children: SemanticFilterItem[] = item.filter_items ?? [];
							if (children.length === 0) {
								const value = getItemValue(item);
								return value !== "" ? [String(value)] : [];
							}
							const leafValues: string[] = [];
							children.forEach((child: SemanticFilterItem) => {
								leafValues.push(...collectLeafValues(child));
							});
							return leafValues;
						};

						const coveredValues = new Set<string>();
						topItems.forEach((item: SemanticFilterItem, idx: number) => {
							const subtreeValues = collectSubtreeValues(item);
							const leafValues = collectLeafValues(item);
							const parentValue = getItemValue(item);
							const parentValueStr = parentValue !== "" ? String(parentValue) : "";
							const parentSelected = parentValueStr !== "" && selectedSet.has(parentValueStr);
							const allLeavesSelected = leafValues.length > 0 && leafValues.every((value) => selectedSet.has(value));
							if (!parentSelected && !allLeavesSelected) return;

							pills.push({
								key: `${name}-parent-${String(parentValue)}-${idx}`,
								label: getItemLabel(item),
								filterName: name,
								focusValue: parentValue,
								onRemove: () => {
									if (!hasQuery) {
										const selectionSet = new Set(params.getAll(name));
										subtreeValues.forEach((value) => selectionSet.delete(value));
										params.delete(name);
										selectionSet.forEach((value) => params.append(name, value));
										updateUrlSearchState(params);
										return;
									}
									const selections = buildSelectionsFromUrl(params);
									const current = Array.isArray(selections[name]) ? selections[name] : [];
									const selectionSet = new Set(current.map((value) => String(value)));
									subtreeValues.forEach((value) => selectionSet.delete(value));
									selections[name] = Array.from(selectionSet);
									void handleApplyFilters(selections);
								},
							});

							subtreeValues.forEach((value) => coveredValues.add(value));
							if (parentValueStr) coveredValues.add(parentValueStr);
						});

						values
							.filter((value) => !coveredValues.has(String(value)))
							.forEach((value, idx) => {
								const label = findLabel(filter.filter_items ?? [], value) ?? String(value);
								pills.push({
									key: `${name}-${String(value)}-${idx}`,
									label,
									filterName: name,
									focusValue: value,
									onRemove: () => {
										if (!hasQuery) {
											const nextValues = params.getAll(name).filter((val) => String(val) !== String(value));
											params.delete(name);
											nextValues.forEach((val) => params.append(name, val));
											updateUrlSearchState(params);
											return;
										}
										const selections = buildSelectionsFromUrl(params);
										const current = Array.isArray(selections[name]) ? selections[name] : [];
										const next = current.filter((val) => String(val) !== String(value));
										selections[name] = next;
										void handleApplyFilters(selections);
									},
								});
							});

						return;
					}

					values.forEach((value, idx) => {
						const label = findLabel(filter.filter_items ?? [], value) ?? String(value);
						pills.push({
							key: `${name}-${String(value)}-${idx}`,
							label,
							filterName: name,
							focusValue: value,
							onRemove: () => {
								if (!hasQuery) {
									const nextValues = params.getAll(name).filter((val) => String(val) !== String(value));
									params.delete(name);
									nextValues.forEach((val) => params.append(name, val));
									updateUrlSearchState(params);
									return;
								}
								const selections = buildSelectionsFromUrl(params);
								const current = Array.isArray(selections[name]) ? selections[name] : [];
								const next = current.filter((val) => String(val) !== String(value));
								selections[name] = next;
								void handleApplyFilters(selections);
							},
						});
					});
					return;
				}

				if (filter.type === "RANGE_FILTER") {
					const fromName = filter.name_from ?? `${name}_from`;
					const toName = filter.name_to ?? `${name}_to`;
					const fromVal = params.get(fromName);
					const toVal = params.get(toName);
					if (fromVal === null && toVal === null) return;

					handledKeys.add(fromName);
					handledKeys.add(toName);

					const min = fromVal !== null && !Number.isNaN(Number(fromVal)) ? Number(fromVal) : undefined;
					const max = toVal !== null && !Number.isNaN(Number(toVal)) ? Number(toVal) : undefined;
					if (min === undefined && max === undefined) return;

					let label: string;
					if (min !== undefined && max !== undefined) {
						label = i18n.t(
							"explore.searchInput.filters.range.both",
							{ min: formatRangeValue(min, filter), max: formatRangeValue(max, filter) },
							{ message: "{min}-{max}" },
						);
					} else if (min !== undefined) {
						label = i18n.t(
							"explore.searchInput.filters.range.min",
							{ value: formatRangeValue(min, filter) },
							{ message: "From {value}" },
						);
					} else {
						label = i18n.t(
							"explore.searchInput.filters.range.max",
							{ value: formatRangeValue(max ?? 0, filter) },
							{ message: "Up to {value}" },
						);
					}

					pills.push({
						key: `${name}-range`,
						label,
						filterName: name,
						onRemove: () => {
							if (!hasQuery) {
								params.delete(fromName);
								params.delete(toName);
								updateUrlSearchState(params);
								return;
							}
							const selections = buildSelectionsFromUrl(params);
							selections[name] = [undefined, undefined];
							void handleApplyFilters(selections);
						},
					});
				}
			});
		}

		const processedRangeBases = new Set<string>();
		const uniqueKeys = new Set<string>();
		for (const key of params.keys()) {
			uniqueKeys.add(key);
		}

		for (const key of uniqueKeys) {
			if (key === "q" || key === "limit" || key === "eqe") continue;
			if (handledKeys.has(key)) continue;

			const isRangeKey = key.endsWith("_from") || key.endsWith("_to");
			if (isRangeKey) {
				const baseName = key.replace(/_(from|to)$/, "");
				if (processedRangeBases.has(baseName)) continue;
				processedRangeBases.add(baseName);

				const fromName = `${baseName}_from`;
				const toName = `${baseName}_to`;
				const fromVal = params.get(fromName);
				const toVal = params.get(toName);
				if (fromVal === null && toVal === null) continue;

				let label: string;
				if (fromVal !== null && toVal !== null) {
					label = `${fromVal}-${toVal}`;
				} else if (fromVal !== null) {
					label = i18n.t("explore.searchInput.filters.range.min", { value: fromVal }, { message: "From {value}" });
				} else {
					label = i18n.t("explore.searchInput.filters.range.max", { value: toVal ?? "" }, { message: "Up to {value}" });
				}

				pills.push({
					key: `${baseName}-range`,
					label,
					filterName: baseName,
					onRemove: () => {
						params.delete(fromName);
						params.delete(toName);
						updateUrlSearchState(params);
					},
				});
				continue;
			}

			const values = params.getAll(key);
			values.forEach((value, idx) => {
				pills.push({
					key: `${key}-${String(value)}-${idx}`,
					label: String(value),
					filterName: key,
					focusValue: value,
					onRemove: () => {
						const nextValues = params.getAll(key).filter((val) => String(val) !== String(value));
						params.delete(key);
						nextValues.forEach((val) => params.append(key, val));
						updateUrlSearchState(params);
					},
				});
			});
		}

		return pills;
	}, [
		filters,
		urlSearch,
		findLabel,
		formatRangeValue,
		buildSelectionsFromUrl,
		handleApplyFilters,
		hasQuery,
		updateUrlSearchState,
	]);
	return (
		<div className={containerClassName}>
			<div className="flex w-full items-start gap-12">
				<div className="relative w-full">
					{useCompact ? (
						<TextField
							className={`${textFieldClassName} compact-search`}
							role="searchbox"
							value={queryValue}
							aria-label={i18n.t({
								id: "explore.searchInput.ariaLabel",
								message: "Search",
							})}
							placeholder={placeholderSuggestion}
							onKeyDown={(event) => {
								isTypingRef.current = true;
								if (event.key === "Enter") {
									event.preventDefault();
									handleSubmit(queryValue);
								}
							}}
							onChange={(event) => handleChange((event.target as HTMLInputElement).value)}
							ref={textFieldRef as React.Ref<HTMLInputElement>}
						>
							<Affix prefix search />
							{queryValue.length > 0 && (
								<Affix
									suffix
									clear
									onClick={handleClearSearch}
									aria-label={i18n.t({ id: "explore.searchInput.clear", message: "Clear text" })}
								/>
							)}
						</TextField>
					) : (
						<>
							<div className="pointer-events-none absolute left-0 top-[-10px] h-full flex items-center pl-16">
								<Affix prefix search />
							</div>
							{queryValue.length > 0 && (
								<div className="absolute right-0 top-[-10px] h-full flex items-center pr-16">
									<Affix
										suffix
										clear
										onClick={handleClearSearch}
										aria-label={i18n.t({ id: "explore.searchInput.clear", message: "Clear text" })}
									/>
								</div>
							)}
							<TextArea
								className={textFieldClassName}
								role="searchbox"
								value={queryValue}
								id="search-input"
								label={i18n.t({
									id: "explore.searchInput.placeholder",
									message: "What are you looking for?",
								})}
								aria-label={i18n.t({
									id: "explore.searchInput.ariaLabel",
									message: "Search",
								})}
								helpText={i18n.t({
									id: "explore.searchInput.helpText",
									message: "Tip! Use descriptive sentences",
								})}
								placeholder={placeholderSuggestion}
								minimumRows={1}
								maximumRows={4}
								onKeyDown={(event) => {
									isTypingRef.current = true;
									if (event.key === "Enter") {
										event.preventDefault();
										handleSubmit(queryValue);
									}
								}}
								onChange={(event) => handleChange((event.target as HTMLTextAreaElement).value)}
								ref={textFieldRef as React.Ref<HTMLTextAreaElement>}
							/>
						</>
					)}
				</div>

				{hasQuery && (
					<Button
						type="button"
						className={`shrink-0 flex items-center gap-4 ${useCompact ? "my-[3px]" : "mt-20"}`}
						secondary
						onClick={() => openFiltersModal()}
						aria-haspopup="dialog"
						aria-expanded={isFiltersModalOpen}
						aria-label={i18n.t({ id: "explore.searchInput.filters", message: "Filters" })}
					>
						<IconFilter16 aria-hidden="true" />
					</Button>
				)}
			</div>
			{filterPills.length > 0 && !useCompact && (
				<div className="flex flex-wrap items-center gap-8">
					{filterPills.map((pill) => (
						<div key={pill.key} className="flex" data-filter-pill>
							<Pill
								suggestion
								className="max-w-[200px]"
								canClose
								onClick={hasQuery ? () => openFiltersModal(pill.filterName, pill.focusValue) : undefined}
								onClose={pill.onRemove}
								label={<span className="inline-block max-w-[160px] truncate align-middle">{pill.label}</span>}
							/>
						</div>
					))}
				</div>
			)}
			<Attention
				placement="bottom-start"
				isShowing={showCallout}
				callout
				canClose
				onDismiss={handleDismissCallout}
				className="max-w-[260px] self-start !pl-0 !pr-0 py-12"
			>
				<p id="callout-search-text">
					{i18n.t({
						id: "explore.searchInput.calloutText",
						message: "Search with a sentence",
					})}
				</p>
			</Attention>
			<div className="w-full flex justify-end">
				{isMobile ? (
					<FiltersModal
						open={isFiltersModalOpen}
						onClose={closeFiltersModal}
						filters={filters}
						isMobile={isMobile}
						currency={currency}
						onApply={handleApplyFilters}
						supportedFilterNames={supportedFilterNames}
						initialActiveFilterName={pendingFilterOpen?.name}
						initialFocusValue={pendingFilterOpen?.focusValue}
					/>
				) : (
					<FiltersModal
						open={hasQuery && isFiltersModalOpen}
						onClose={closeFiltersModal}
						filters={filters}
						isMobile={false}
						currency={currency}
						onApply={handleApplyFilters}
						supportedFilterNames={supportedFilterNames}
						initialActiveFilterName={pendingFilterOpen?.name}
						initialFocusValue={pendingFilterOpen?.focusValue}
					/>
				)}
			</div>
		</div>
	);
}
